import { useLayoutEffect } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { Stack, useNavigation } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text } from "@src/components";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { isWeb } from "@src/utils/web-helpers";
import {
  ExceptionHeaderTitle,
  InlineSaveButton,
  ExceptionSummaryCard,
  CamerasSectionHeader,
  EmptyIncludedCard,
  CamerasGrid,
} from "@src/features/alarms/components/EditExceptionShared";
import { useEditException, TRANSITION_MS } from "./useEditException";

export default function EditExceptionScreen() {
  const { theme } = useUnistyles();
  const { bottom } = useSafeAreaInsets();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();

  const {
    isNew,
    exceptionData,
    included,
    available,
    transitioning,
    tz,
    cameraLabel,
    isSaving,
    hasChanges,
    handleSave,
    toggleCamera,
    removeAll,
    includeAll,
    navigateToDetails,
  } = useEditException();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerBackButtonDisplayMode: "minimal",
      headerStyle: { backgroundColor: theme.colors.background },
      headerTintColor: theme.colors.text,
      headerLeft,
      headerTitle: () => (
        <ExceptionHeaderTitle isNew={isNew} isSaving={isSaving} hasChanges={hasChanges} onSave={handleSave} />
      ),
    });
  }, [navigation, headerLeft, isNew, isSaving, hasChanges, handleSave, theme.colors.background, theme.colors.text]);

  if (!exceptionData)
    return (
      <View flex={1} justifyContent="center" alignItems="center" gap="md">
        <ActivityIndicator size="large" color={theme.colors.textMuted} />
        <Text textStyle="md/regular" color="textMuted">
          Loading exception data...
        </Text>
      </View>
    );

  return (
    <>
      <Stack.Screen options={{ title: isNew ? "New exception" : "Edit Exception" }} />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.contentContainerStyle, { paddingBottom: bottom + theme.spacing["6xl"] }]}
        showsVerticalScrollIndicator={false}
      >
        {isWeb && <InlineSaveButton isSaving={isSaving} hasChanges={hasChanges} onSave={handleSave} />}

        <ExceptionSummaryCard
          exceptionData={exceptionData}
          cameraLabel={cameraLabel}
          tz={tz}
          isSaving={isSaving}
          onPress={navigateToDetails}
        />

        <View mt="7xl">
          <CamerasSectionHeader
            title="Included"
            count={included.length}
            bulkActionLabel="Remove all"
            onBulkAction={removeAll}
            isSaving={isSaving}
            transitionMs={TRANSITION_MS}
          />
          {included.length === 0 && transitioning.size === 0 && <EmptyIncludedCard transitionMs={TRANSITION_MS} />}
          <CamerasGrid
            primary={included}
            skeletonSource={available}
            transitioning={transitioning}
            selected={true}
            isSaving={isSaving}
            onToggle={toggleCamera}
            transitionMs={TRANSITION_MS}
          />
        </View>

        <View mt="7xl">
          <CamerasSectionHeader
            title="Available"
            count={available.length}
            bulkActionLabel="Include all"
            onBulkAction={includeAll}
            isSaving={isSaving}
            transitionMs={TRANSITION_MS}
          />
          <CamerasGrid
            primary={available}
            skeletonSource={included}
            transitioning={transitioning}
            selected={false}
            isSaving={isSaving}
            onToggle={toggleCamera}
            transitionMs={TRANSITION_MS}
            skeletonFirst
          />
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  contentContainerStyle: {
    paddingHorizontal: theme.spacing["3xl"],
    paddingTop: theme.spacing["4xl"],
    flexGrow: 1,
  },
}));
